import { useState, useEffect, useCallback, useRef } from 'react';
import { useToast } from '../ui/Toast';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

const StatBadge = ({ label, value, color = 'text-slate-200' }) => (
  <div className="text-center">
    <div className={`text-lg font-bold ${color}`}>{value}</div>
    <div className="text-xs text-slate-500">{label}</div>
  </div>
);

const EdgeCard = ({ item, type }) => {
  const isGood = type === 'edge';
  const pct = (item.winRate * 100).toFixed(0);
  const deltaPct = (Math.abs(item.delta) * 100).toFixed(0);

  return (
    <div className={`p-3 rounded-lg border ${isGood
      ? 'bg-emerald-500/5 border-emerald-500/20'
      : 'bg-amber-500/5 border-amber-500/20'
    }`}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm font-medium text-slate-200">
          {item.dimension === 'bySymbol' ? item.key : formatKey(item)}
        </span>
        <span className={`text-xs font-bold ${isGood ? 'text-emerald-400' : 'text-amber-400'}`}>
          {isGood ? '+' : '-'}{deltaPct}% vs avg
        </span>
      </div>
      <div className="flex items-center gap-3 text-xs text-slate-400">
        <span>{pct}% win rate</span>
        <span>•</span>
        <span>{item.count} trades</span>
        <span>•</span>
        <span className={item.avgPnl >= 0 ? 'text-emerald-400' : 'text-red-400'}>
          avg ${item.avgPnl >= 0 ? '+' : ''}{item.avgPnl?.toFixed(0)}/trade
        </span>
      </div>
    </div>
  );
};

function formatKey(item) {
  const dim = item.dimension;
  const k = item.key;
  if (dim === 'byHour') return `Hour ${k} trades`;
  if (dim === 'byDayOfWeek') return ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][k] || k;
  if (dim === 'bySession') return `${k} session`;
  if (dim === 'bySide') return `${k} trades`;
  if (dim === 'byEmotionalState') return `${k} emotional state`;
  if (dim === 'byDuration') return `${k} hold time`;
  return k;
}

const EdgeReport = () => {
  const { user } = useAuth();
  const toast = useToast();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const hasFetched = useRef(false);

  const tier = user?.subscription_tier || user?.subscriptionTier || 'free';

  const isLlmError = (err) => {
    const status = err.response?.status;
    const msg = (err.response?.data?.error || err.message || '').toLowerCase();
    return (
      status === 401 ||
      msg.includes('api key') ||
      msg.includes('llm') ||
      msg.includes('not configured') ||
      msg.includes('openai') ||
      msg.includes('anthropic')
    );
  };

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get('/ai/edge-report');
      if (data.success) setReport(data);
    } catch (err) {
      if (err.response?.status === 403) {
        setError('elite');
      } else if (isLlmError(err)) {
        setError('no_llm');
      } else {
        setError(err.response?.data?.error || 'Failed to load AI edge report.');
        // Only show toast once per mount, not on every retry
        if (!hasFetched.current) {
          toast.error('Failed to load edge report.');
        }
      }
    } finally {
      setLoading(false);
      hasFetched.current = true;
    }
  }, []); // eslint-disable-line

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  if (loading) {
    return (
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-lg bg-purple-500/10 flex items-center justify-center">🤖</div>
          <h3 className="text-slate-200 font-semibold">AI Edge Report</h3>
        </div>
        <div className="space-y-2 animate-pulse">
          <div className="h-4 bg-slate-700 rounded w-3/4" />
          <div className="h-4 bg-slate-700 rounded w-1/2" />
          <div className="h-4 bg-slate-700 rounded w-2/3" />
        </div>
      </div>
    );
  }

  if (error === 'no_llm') {
    return (
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-8 h-8 rounded-lg bg-purple-500/10 flex items-center justify-center">🤖</div>
          <h3 className="text-slate-200 font-semibold">AI Edge Report</h3>
        </div>
        <p className="text-slate-400 text-sm">
          AI Edge Report requires an LLM provider. Go to <strong className="text-slate-300">Settings → AI</strong> to connect your OpenAI or Anthropic API key.
        </p>
      </div>
    );
  }

  if (error && error !== 'elite') {
    return (
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-8 h-8 rounded-lg bg-purple-500/10 flex items-center justify-center">🤖</div>
          <h3 className="text-slate-200 font-semibold">AI Edge Report</h3>
        </div>
        <p className="text-red-400 text-sm mb-3">{error}</p>
        <button
          onClick={() => { hasFetched.current = false; fetchReport(); }}
          className="text-xs text-slate-400 hover:text-slate-200 transition-colors"
        >
          ↻ Retry
        </button>
      </div>
    );
  }

  if (!report) return null;

  if (report.insufficient) {
    return (
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-3">
          <div className="text-2xl">🤖</div>
          <h3 className="text-slate-200 font-semibold">AI Edge Report</h3>
        </div>
        <p className="text-slate-400 text-sm">{report.message}</p>
      </div>
    );
  }

  const expiresIn = report.expiresAt
    ? Math.round((new Date(report.expiresAt) - Date.now()) / 3600000)
    : null;

  return (
    <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6 space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-purple-500/10 flex items-center justify-center">🤖</div>
          <div>
            <h3 className="text-slate-200 font-semibold">AI Edge Report</h3>
            <p className="text-xs text-slate-400">
              {report.tradeCount} trades analyzed
              {report.cached && expiresIn && ` · refreshes in ~${expiresIn}h`}
            </p>
          </div>
        </div>
        <button
          onClick={fetchReport}
          disabled={loading || (report.cached && expiresIn > 0)}
          className="text-xs text-slate-400 hover:text-slate-200 disabled:opacity-40 transition-colors"
          title={report.cached ? `Report refreshes in ${expiresIn}h` : 'Regenerate'}
        >
          ↻ {report.cached ? 'Cached' : 'Regenerate'}
        </button>
      </div>

      {/* Overall stats */}
      {report.overall && (
        <div className="grid grid-cols-4 gap-4 bg-slate-700/20 rounded-lg p-3">
          <StatBadge label="Win Rate" value={`${(report.overall.winRate * 100).toFixed(0)}%`} color={report.overall.winRate >= 0.5 ? 'text-emerald-400' : 'text-red-400'} />
          <StatBadge label="Avg P&L" value={`$${report.overall.avgPnl?.toFixed(0)}`} color={report.overall.avgPnl >= 0 ? 'text-emerald-400' : 'text-red-400'} />
          <StatBadge label="Total P&L" value={`$${report.overall.totalPnl?.toFixed(0)}`} color={report.overall.totalPnl >= 0 ? 'text-emerald-400' : 'text-red-400'} />
          <StatBadge label="Trades" value={report.tradeCount} />
        </div>
      )}

      {/* Top Edges */}
      {report.edges?.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-emerald-400 uppercase tracking-wider mb-2">✦ Your Top Edges</h4>
          <div className="space-y-2">
            {report.edges.map((e, i) => <EdgeCard key={i} item={e} type="edge" />)}
          </div>
        </div>
      )}

      {/* Weaknesses */}
      {report.warnings?.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-amber-400 uppercase tracking-wider mb-2">⚠ Watch Out For</h4>
          <div className="space-y-2">
            {report.warnings.map((w, i) => <EdgeCard key={i} item={w} type="warning" />)}
          </div>
        </div>
      )}

      {/* AI Narrative Rules */}
      {report.narrative?.rules?.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-purple-400 uppercase tracking-wider mb-2">📋 AI-Generated Rules</h4>
          <div className="space-y-2">
            {report.narrative.rules.map((rule, i) => (
              <div key={i} className="flex items-start gap-2 text-sm text-slate-300">
                <span className="text-purple-400 font-bold shrink-0">{i + 1}.</span>
                <span>{rule}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* AI Disclaimer */}
      <div className="text-[10px] text-slate-600 border-t border-slate-700/50 pt-3 mt-2 leading-relaxed">
        AI-generated analysis is for informational purposes only and does not constitute financial advice.
        Outputs may be inaccurate or misleading. Past performance is not indicative of future results.
        Always conduct your own research before making trading decisions.
      </div>
    </div>
  );
};

export default EdgeReport;
